import { notification } from 'antd';
import moment from 'moment';
import { routerRedux } from 'dva/router';

import { createTestCase, getLinkAccounts, linkAccount, unlinkAccount } from '../../services/adsTracer';
import { ADS_TRACER } from '../../constants';
import { arrToMap, dedupArr } from '../../utils';
import { ESPN_PUBLISHER } from '../../pages/AdsTracer/constants';

export const accountProfileCombinationsSelector = ({ newTestCase }) => newTestCase.accountProfileCombinations;

export const initState = {
  accountProfileCombinations: [],
};

export const newTestCase = {
  namespace: 'newTestCase',
  state: initState,
  reducers: {
    updateAccountProfileCombinations(state, { payload: accountProfileCombinations }) {
      return { ...state, accountProfileCombinations };
    },
    clearState() {
      return initState;
    },
  },
  effects: {
    *pageInit(emptyPayload, { put }) {
      yield put({ type: 'clearState' });
      yield put({ type: 'fetchLinkedAccounts' });
    },
    *fetchLinkedAccounts(emptyPayload, { call, put }) {
      const resp = yield call(getLinkAccounts);

      // Received Linked Accounts
      if (resp?.status === 200 && resp?.data) {
        let accountProfileCombinations = [];
        resp.data.forEach(
          ({
            id: accountId,
            email,
            'first-name': accountName,
            subscription,
            profiles,
            publisher,
            channel,
            'distro-partner': distroPartner,
            'ip-address': ipAddress,
            'device-ifa': deviceIfa,
            'fubo-user-id': fuboUserId,
          }) => {
            profiles.forEach(({ id: profileId, name: profileName }) => {
              accountProfileCombinations.push({
                key: `${accountId}#${profileId}#${profileName}`,
                accountId,
                email,
                accountName,
                subscription,
                profileId,
                profileName,
                publisher,
                channel,
                distroPartner,
                ipAddress,
                deviceIfa,
                fuboUserId,
              });
            });
          }
        );

        yield put({ type: 'updateAccountProfileCombinations', payload: accountProfileCombinations });
      } else if (resp?.status === 204) {
        yield put({ type: 'updateAccountProfileCombinations', payload: [] });
      }
    },
    *verifySelectedAccounts({ payload: { targetKeys, currentStep } }, { call, put, select }) {
      const hasSelectedAccountsForNewTestCases = targetKeys.length === 0;

      // No accounts selected, throw validation message
      if (hasSelectedAccountsForNewTestCases) {
        yield call(notification.error, {
          message: 'Validation Error',
          description: 'Please select at least one account-profile for testing',
          duration: 3,
        });
        return false;
      }
      return true;
    },
    *createNewTestCase({ payload: { targetKeys, endAt } }, { call, put, select }) {
      const isExpirationTimeValid = endAt > moment();

      // Validate expiration time has been set
      if (isExpirationTimeValid) {
        const profiles = yield select(accountProfileCombinationsSelector);

        const newTestCases = profiles
          .filter(({ accountId, profileId, profileName }) =>
            targetKeys.includes(`${accountId}#${profileId}#${profileName}`)
          )
          .map((profile) => {
            return {
              'account-id': profile.accountId.toString(),
              'profile-id': profile.profileId.toString(),
              publisher: profile.publisher,
              'end-time': endAt,
              ...(profile.channel && { channel: profile.channel }),
              ...(profile.distroPartner && { 'distro-partner': profile.distroPartner }),
              ...(profile.accountName && { nickname: profile.accountName }),
              ...(profile.ipAddress && { 'ip-address': profile.ipAddress }),
              ...(profile.deviceIfa && { 'device-ifa': profile.deviceIfa }),
              ...(profile.fuboUserId && { 'fubo-user-id': profile.fuboUserId }),
            };
          });

        // Call Ads Tracer to create the new test case (errHandler in service shows notification and rethrows)
        let res;
        try {
          res = yield call(createTestCase, { 'test-cases': newTestCases });
        } catch (e) {
          console.error('Failed to create test case:', e);
          return;
        }
        if (res?.data) {
          yield put(routerRedux.push('/' + ADS_TRACER));
          yield put({ type: 'clearState' });
          yield call(notification.success, {
            message: 'New Test Case(s) Created',
            duration: 3,
          });
        }

        // Invalid Expiration time
      } else {
        yield call(notification.error, {
          message: 'Validation Error',
          description: 'Please select a valid expiration time for the test case',
          duration: 3,
        });
      }
    },
    *addAccountId({ payload: accountId, profileId, nickname, publisher, channel, distroPartner }, { call, put }) {
      const linkAccountResult = yield call(
        linkAccount,
        accountId,
        [profileId],
        nickname,
        publisher,
        channel,
        distroPartner
      );

      if (linkAccountResult?.data) {
        yield put({ type: 'fetchLinkedAccounts' });
        yield call(notification.success, {
          message: 'Account Linked',
          description: `Account ${linkAccountResult.data['first-name']} linked`,
          duration: 5,
        });
        return true;
      }
      return false;
    },
    *addEspnSwidAccount({ payload: swid, nickname }, { call, put }) {
      const linkAccountResult = yield call(linkAccount, swid, [], nickname, ESPN_PUBLISHER);

      if (linkAccountResult?.data) {
        yield put({ type: 'fetchLinkedAccounts' });
        yield call(notification.success, {
          message: 'Account Linked',
          description: `Account ${linkAccountResult.data['first-name']} linked`,
          duration: 5,
        });
        return true;
      }
      return false;
    },
    *addIpAccount({ payload: ip, nickname, channel, distroPartner }, { call, put }) {
      const linkAccountResult = yield call(
        linkAccount,
        undefined,
        [],
        nickname,
        'OFF_PLATFORM',
        channel,
        distroPartner,
        ip
      );
      if (linkAccountResult?.data) {
        yield put({ type: 'fetchLinkedAccounts' });
        yield call(notification.success, {
          message: 'Account Linked',
          description: `Account ${linkAccountResult.data['first-name']} linked`,
          duration: 5,
        });
        return true;
      }
      return false;
    },
    *removeAccount({ payload: selectedProfilesToRemove }, { call, put, select }) {
      const fetchedLinkedAccounts = yield select(accountProfileCombinationsSelector);

      const mappedAccounts = arrToMap(fetchedLinkedAccounts, 'profileId');
      let ids = [];
      selectedProfilesToRemove.sourceSelectedKeys.forEach((profileId) => {
        if (mappedAccounts[profileId]) {
          ids.push(mappedAccounts[profileId].accountId);
        }
      });
      const accountIds = dedupArr(ids);

      for (const id of accountIds) {
        (yield call(unlinkAccount, id)) &&
          (yield put({ type: 'fetchLinkedAccounts' })) &&
          (yield call(notification.success, {
            message: 'Account unlink successful',
            description: `User ${id} account unlinked`,
            duration: 5,
          }));
      }
    },
    *addNewsEntAccount({ payload: { swid, nickname, app } }, { call, put }) {
      const linkAccountResult = yield call(require('../../services/adsTracer').linkNewsEntAccount, swid, nickname, app);
      if (linkAccountResult?.data) {
        yield put({ type: 'fetchLinkedAccounts' });
        yield call(require('antd').notification.success, {
          message: 'Account Linked',
          description: `News & Entertainment Account ${nickname} linked`,
          duration: 5,
        });
        return true;
      }
      return false;
    },
    *addRokuFastAccount({ payload: { deviceIfa, nickname } }, { call, put }) {
      const linkAccountResult = yield call(
        require('../../services/adsTracer').linkRokuFastAccount,
        deviceIfa,
        nickname
      );
      if (linkAccountResult?.data) {
        yield put({ type: 'fetchLinkedAccounts' });
        yield call(require('antd').notification.success, {
          message: 'Account Linked',
          description: `Roku FAST Account ${nickname} linked`,
          duration: 5,
        });
        return true;
      }
      return false;
    },
    *addFuboAccount({ payload: { fuboUserId, nickname } }, { call, put }) {
      const linkAccountResult = yield call(require('../../services/adsTracer').linkFuboAccount, fuboUserId, nickname);
      if (linkAccountResult?.data) {
        yield put({ type: 'fetchLinkedAccounts' });
        yield call(require('antd').notification.success, {
          message: 'Account Linked',
          description: `Fubo Account ${nickname} linked`,
          duration: 5,
        });
        return true;
      }
      return false;
    },
  },
};
